import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PushToggle from "@/components/PushToggle";
import { Bell, CheckCheck, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function AssocNotifications() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/notifications")).data);
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAllRead = async () => {
    const unread = items.filter(n => !n.read);
    await Promise.all(unread.map(n => api.post(`/notifications/${n.id}/read`)));
    load();
  };

  const unreadCount = items.filter(n => !n.read).length;

  return (
    <div className="space-y-6" data-testid="assoc-notifications">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Avisos</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Notificacions</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {unreadCount > 0 ? `Tens ${unreadCount} avís${unreadCount === 1 ? "" : "os"} sense llegir` : "Estàs al dia."}
          </p>
        </div>
        <div className="flex gap-2">
          <PushToggle />
          {unreadCount > 0 && (
            <Button variant="outline" className="rounded-full" onClick={markAllRead} data-testid="notif-mark-all-read">
              <CheckCheck className="w-4 h-4 mr-2" aria-hidden="true" /> Marca tot com llegit
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {items.map(n => (
          <Card key={n.id} className={`rounded-2xl border-2 ${n.read ? "opacity-60" : ""}`} data-testid={`notif-item-${n.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${n.read ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
                <Bell className="w-4 h-4" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{n.title}</div>
                  {!n.read && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">Nou</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{n.body}</div>
                <div className="text-xs text-muted-foreground mt-2">{new Date(n.created_at).toLocaleString()}</div>
                <div className="mt-3 flex gap-2">
                  {n.link && (
                    <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`notif-open-${n.id}`}>
                      <Link to={n.link.startsWith("http") ? "#" : n.link} onClick={() => n.link.startsWith("http") && window.open(n.link, "_blank")}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Obrir
                      </Link>
                    </Button>
                  )}
                  {!n.read && (
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => markRead(n.id)} data-testid={`notif-read-${n.id}`}>
                      <CheckCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Marca com llegit
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Bell className="w-12 h-12 mx-auto opacity-30 mb-3" aria-hidden="true" />
            <div className="text-sm">Encara no tens notificacions.</div>
          </div>
        )}
      </div>
    </div>
  );
}
